import React from 'react';
import { Link } from 'react-router-dom';
import styles from './Vision.module.css';

export default function Vision() {
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>Consolidate. Curate. Share. Publish.</h1>

        <div className={styles.hero}>
          <p className={styles.heroLead}>
            MyPeopleDatabase helps you consolidate your people data, curate what matters, share
            curated data privately with groups, and publish your curated identity at one URL.
          </p>
          <p className={styles.heroSub}>
            Not a social network. Not a CRM. A privacy-first system you control.
          </p>
          <div className={styles.ctaRow}>
            <Link to="/buy" className={styles.primaryCta}>
              Buy Now →
            </Link>
            <Link to="/mpd-id" className={styles.secondaryCta}>
              MPD ID Page →
            </Link>
          </div>
        </div>

        <div className={styles.narrative}>
          <h2 className={styles.subtitle}>What you get</h2>
          <div className={styles.cardGrid}>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Consolidate</div>
              <div className={styles.cardBody}>
                Bring scattered notes, contact details, and relationship context into one place you
                own.
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Curate</div>
              <div className={styles.cardBody}>
                Keep the truth about your relationships—what matters, what changed, and why it
                matters.
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Group & share privately</div>
              <div className={styles.cardBody}>
                Share curated data with specific groups while keeping full control over what’s
                visible.
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Publish one URL</div>
              <div className={styles.cardBody}>
                Publish your curated identity at a single link (your MPD ID Page) you can share
                anywhere.
              </div>
            </div>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.subtitle}>Now / Next / Later</h2>
            <div className={styles.roadmapGrid}>
              <div className={styles.roadmapCard}>
                <div className={styles.roadmapTitle}>Now</div>
                <ul className={styles.featureList}>
                  <li>Consolidate and curate your people data in a system you control.</li>
                  <li>Organize by groups and context for fast recall.</li>
                  <li>Publish your curated identity at one URL (MPD ID Page).</li>
                </ul>
              </div>
              <div className={styles.roadmapCard}>
                <div className={styles.roadmapTitle}>Next</div>
                <ul className={styles.featureList}>
                  <li>Private sharing groups for curated data distribution.</li>
                  <li>Improved publishing controls: draft vs public, visibility toggles.</li>
                  <li>Faster onboarding and import/export workflows.</li>
                </ul>
              </div>
              <div className={styles.roadmapCard}>
                <div className={styles.roadmapTitle}>Later</div>
                <ul className={styles.featureList}>
                  <li>Timeline views to revisit how relationships evolve.</li>
                  <li>AI-assisted reminders and follow-up suggestions.</li>
                  <li>Deeper identity publishing and verification workflows.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.subtitle}>Built for every platform, secured for the future</h2>
            <div className={styles.cardGrid}>
              <div className={styles.card}>
                <div className={styles.cardTitle}>All Platforms, Including Mobile</div>
                <div className={styles.cardBody}>
                  MyPeopleDatabase is designed to work wherever you are—desktop, web, and mobile.
                  Full-featured access to your people data on every device you use.
                </div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardTitle}>AI Integration</div>
                <div className={styles.cardBody}>
                  Intelligent assistants to help you recall context, surface follow-ups, and keep
                  your relationships active—without giving up control of your data.
                </div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Advanced Cybersecurity</div>
                <div className={styles.cardBody}>
                  End-to-end encryption, granular access controls, and enterprise-grade security
                  options to keep your most sensitive relationship data protected.
                </div>
              </div>
            </div>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.subtitle}>Powered by the Polymodality Project</h2>
            <p>
              People are inherently complex. The same person is an <em>employee</em> in an HR
              system, a <em>customer</em> in a sales tool, a <em>contact</em> in your phone, and a
              <em> friend</em> in real life—each context carrying different data, different names,
              and different meaning. Traditional software handles this by duplicating data, merging
              incompatible fields, or simply losing context. The result is scattered, inconsistent,
              and incomplete people information.
            </p>
            <p>
              The <strong>Polymodality Project</strong> is a new software paradigm built on the
              principle that a single entity can exist across many contexts simultaneously, each with
              its own clean, precise model. Rather than forcing one unified "person" record that
              silently mixes unrelated fields—leaving some null, some stale, some wrong—Polymodality
              treats each context as a first-class view of the same underlying identity. Models from
              different contexts can be <em>composed</em> precisely, without muddying each other.
            </p>
            <p>
              MyPeopleDatabase applies this foundation directly to your people data. Every person
              you track can carry context-aware information—their professional identity, personal
              details, shared group data, and published profile—each kept clean and accurate rather
              than collapsed into a single bloated record. The Polymodality framework is what makes
              it possible to consolidate the complex ways people information exists across your
              life, without losing the distinctions that make that information meaningful.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.subtitle}>Why this matters</h2>
            <p>
              <strong>True Data Ownership:</strong> your relationships and personal connections are
              valuable assets. MyPeopleDatabase is built on the principle that your network belongs
              exclusively to you—to manage, control, and preserve on your terms.
            </p>
            <p className={styles.closing}>
              This is not just about who we know—but about remembering why they matter.
            </p>
          </div>

          <div className={styles.linksSection}>
            <Link to="/buy" className={styles.linkButton}>
              Get Started (Buy Now) →
            </Link>
            <Link to="/features" className={styles.linkButton}>
              Explore Features →
            </Link>
            <Link to="/mpd-id" className={styles.linkButton}>
              Learn about MPD ID Page →
            </Link>
            <Link to="/data-principles" className={styles.linkButton}>
              Data Ownership Principles →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
